import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, ShieldCheck, Users, Sparkles, ClipboardCheck, FileBarChart, Mail, PhoneCall } from "lucide-react";

/**
 * Public marketing/landing page mounted at "/" for unauthenticated visitors.
 * Authenticated users are auto-redirected to their protected dashboard.
 *
 * Design: full-viewport looping muted video hero + navy overlay, followed by
 * concise product-value sections, real client badges, and a sign-in CTA.
 * No fake stats; no invented testimonials.
 */
export default function LandingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const videoRef = useRef(null);

    // Auth-aware redirect: if a logged-in user lands here (e.g. from a bookmark), send them home.
    useEffect(() => {
        if (user) navigate("/", { replace: false });
    }, [user, navigate]);

    // Attempt autoplay on mount — muted + playsInline is required on iOS Safari.
    // If autoplay is blocked or the source fails, the <video> element's native
    // `poster` image stays visible so the hero never looks broken.
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => { /* poster remains */ });
    }, []);

    const clients = ["Bayer", "Bosch", "Susonity", "Nuvama", "Roche", "SMIFS"];

    return (
        <div className="min-h-screen bg-[#0d1a2e] text-white" data-testid="landing-page">
            {/* --------- HEADER --------- */}
            <header className="fixed top-0 inset-x-0 z-30 bg-gradient-to-b from-black/60 via-black/30 to-transparent">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-[#E02B2B] flex items-center justify-center font-black text-white">F</div>
                        <div className="leading-tight">
                            <div className="text-sm font-semibold tracking-wide">Flexworx Ops</div>
                            <div className="text-[10px] text-white/70 uppercase tracking-widest">A Flexworx Company</div>
                        </div>
                    </div>
                    <Link to="/login" className="inline-flex items-center gap-1 bg-white/95 hover:bg-white text-[#1E3355] font-semibold text-sm px-4 py-2 rounded-full transition" data-testid="landing-signin-btn">
                        Sign in <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </header>

            {/* --------- HERO with looping video --------- */}
            <section className="relative overflow-hidden" style={{ minHeight: "100vh" }} data-testid="landing-hero">
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    poster="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=70"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    data-testid="landing-hero-video"
                >
                    {/* WebM first for engines that decline H.264 (headless Chromium,
                        some Linux Firefox builds); MP4 fallback for Safari/older iOS
                        that don't ship a WebM decoder. Self-hosted so the SW never
                        needs to touch a cross-origin media URL. */}
                    <source src="/videos/hero.webm" type='video/webm; codecs="vp9"' />
                    <source src="/videos/hero.mp4" type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
                </video>
                {/* Navy overlay for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#1E3355]/70 via-[#1E3355]/60 to-[#0d1a2e]/95" />
                <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-32 sm:pt-40 pb-24 sm:pb-32">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-widest text-white/85 border border-white/20 mb-6">
                            <Sparkles className="w-3.5 h-3.5" /> Managed Office Services · Kolkata
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight">
                            Corporate offices, run <span className="text-[#E02B2B]">flawlessly</span>. Every single day.
                        </h1>
                        <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
                            Flexworx is Kolkata's specialist in fully-managed office services — manpower, housekeeping,
                            security, reception, consumables, tea &amp; coffee service. Flexworx Ops is the platform that
                            keeps every site accountable, evidenced and billable.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/login" className="inline-flex items-center gap-2 bg-[#E02B2B] hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-full text-sm shadow-lg" data-testid="landing-signin-cta">
                                Sign in to your dashboard <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a href="#platform" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full text-sm border border-white/20">
                                What the platform does
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------- WHAT THE PLATFORM DOES --------- */}
            <section id="platform" className="relative bg-[#F4F6F9] text-[#1E3355] py-16 sm:py-24">
                <div className="mx-auto max-w-6xl px-4 sm:px-6">
                    <div className="max-w-2xl mb-10">
                        <div className="text-[11px] uppercase tracking-widest text-[#E02B2B] font-bold">The platform</div>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-2 leading-tight">
                            Every task, every site, every day — in one accountable trail.
                        </h2>
                        <p className="text-gray-600 mt-3 text-base leading-relaxed">
                            Flexworx Ops replaces paper registers with a mobile-first system field teams actually use,
                            and gives head office the evidence trail that stands up to any client audit.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            { icon: ClipboardCheck, title: "Daily checklists per site", body: "Configurable per client — critical vs standard tasks, sign-off with reasoning, immutable once locked." },
                            { icon: ShieldCheck, title: "Photo evidence + SLA tracking", body: "Photos captured at the moment of work; SLA compliance graphed month-over-month." },
                            { icon: Users, title: "Roster, attendance, cover", body: "Live attendance with cover arrangements, absence-rule violations flagged same day." },
                            { icon: FileBarChart, title: "Client-ready monthly reports", body: "Auto-drafted from actual site activity — reception, housekeeping, security, consumables, contracted services." },
                            { icon: Sparkles, title: "Consumables + procurement", body: "Register with reorder thresholds, PR workflow, weekly reorder cadence, monthly reconciliation." },
                            { icon: Mail, title: "Escalations that don't vanish", body: "Every escalation gets a linked service request, ticked off only when closed — with client acknowledgement." },
                        ].map((f) => (
                            <div key={f.title} className="rounded-xl bg-white border border-gray-100 p-5 shadow-sm">
                                <div className="w-10 h-10 rounded-lg bg-[#1E3355] text-white flex items-center justify-center mb-3">
                                    <f.icon className="w-5 h-5" />
                                </div>
                                <div className="font-semibold text-[#1E3355]">{f.title}</div>
                                <div className="text-sm text-gray-600 mt-1 leading-relaxed">{f.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --------- CLIENT BADGES --------- */}
            <section className="relative bg-white text-[#1E3355] py-14 border-y border-gray-100">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 text-center">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Trusted at</div>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-testid="landing-clients">
                        {clients.map((c) => (
                            <span key={c} className="inline-flex items-center px-4 py-2 rounded-full bg-[#F4F6F9] border border-gray-200 text-sm font-semibold tracking-wide">
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* --------- CTA --------- */}
            <section className="relative bg-[#1E3355] text-white py-16 sm:py-20">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
                    <h2 className="font-display text-2xl sm:text-4xl font-semibold leading-tight">
                        Bring your office up to Flexworx standard.
                    </h2>
                    <p className="mt-4 text-white/85 text-base max-w-2xl mx-auto leading-relaxed">
                        Talk to us about handing over your office operations. If you're already a client, sign in to your
                        team's dashboard below.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <Link to="/login" className="inline-flex items-center gap-2 bg-[#E02B2B] hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-full text-sm shadow-lg" data-testid="landing-cta-signin">
                            Sign in <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a href="mailto:info@myflexworx.com" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full text-sm border border-white/20">
                            <Mail className="w-4 h-4" /> info@myflexworx.com
                        </a>
                    </div>
                </div>
            </section>

            {/* --------- FOOTER --------- */}
            <footer className="bg-[#0d1a2e] text-white/70 py-10">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 flex flex-wrap items-center justify-between gap-4 text-xs">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-[#E02B2B] flex items-center justify-center font-black text-white text-xs">F</div>
                        <div>
                            <div className="font-semibold text-white">Flexworx Ops · A Flexworx Company</div>
                            <div className="text-white/60">Kolkata · HAG Realty LLP</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-5">
                        <a href="https://flexworxcare.com" target="_blank" rel="noreferrer" className="hover:text-white">flexworxcare.com</a>
                        <a href="https://myflexworx.com" target="_blank" rel="noreferrer" className="hover:text-white">myflexworx.com</a>
                        <a href="mailto:info@myflexworx.com" className="inline-flex items-center gap-1 hover:text-white"><Mail className="w-3.5 h-3.5" /> info@myflexworx.com</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
